import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Shield, User, Mail, Phone, Briefcase, Linkedin, Building, Globe, MapPin, Users, Target, Cpu } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ReCAPTCHA from "react-google-recaptcha";

const recaptchaSiteKey = import.meta.env.VITE_GOOGLE_CAPTCHA_KEY;

let apiUrl = "https://api.gimmefy.ai/v2";

console.log("import.meta.env.VITE_API_URL", import.meta.env.VITE_API_URL)
const baseUrl = window.location.origin;
if (baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}

const EnterpriseQuoteForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: "",
    workEmail: "",
    phone: "",
    jobTitle: "",
    linkedinProfile: "",
    // Step 2: Company Details
    companyName: "",
    companyUrl: "",
    location: "",
    // Step 3: Your Requirements
    numberOfSeats: "",
    primaryUseCase: "",
    usingAiTools: "",
    additionalRequirements: ""
  });

  // Handle reCAPTCHA change
  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const validateStep = (step: number): boolean => {
    if (step === 1) {
      if (!formData.name || !formData.workEmail || !formData.phone || !formData.jobTitle || !formData.linkedinProfile) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        return false;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.workEmail)) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address.",
          variant: "destructive"
        });
        return false;
      }
    } else if (step === 2) {
      if (!formData.companyName) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        return false;
      }
    } else if (step === 3) {
      if (!formData.numberOfSeats || !formData.primaryUseCase) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        return false;
      }
      // Check if reCAPTCHA is completed
      if (!captchaValue) {
        toast({
          title: "reCAPTCHA Error",
          description: "Please complete the reCAPTCHA verification",
          variant: "destructive"
        });
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(3)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        event_name: "USER_CONTACT_FROM_ENTERPRISE_FORM",
        payload: {
          name: formData.name,
          work_email: formData.workEmail,
          phone: formData.phone,
          job_title: formData.jobTitle,
          linkedin_profile: formData.linkedinProfile,
          company_name: formData.companyName,
          company_url: formData.companyUrl || null,
          location: formData.location || null,
          number_of_seats: formData.numberOfSeats,
          primary_use_case: formData.primaryUseCase,
          using_ai_tools: formData.usingAiTools || null,
          additional_requirements: formData.additionalRequirements || null,
          timestamp: new Date().toISOString(),
        },
        captcha_code: captchaValue
      };

      const response = await fetch(`${apiUrl}/triggerNotificationPublic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }

      toast({
        title: "Quote Requested!",
        description: "Our enterprise team will contact you within 24 hours.",
        action: <div style={{ display: 'none' }} />,
      });

      // Reset form and reCAPTCHA
      setFormData({
        name: "",
        workEmail: "",
        phone: "",
        jobTitle: "",
        linkedinProfile: "",
        companyName: "",
        companyUrl: "",
        location: "",
        numberOfSeats: "",
        primaryUseCase: "",
        usingAiTools: "",
        additionalRequirements: ""
      });
      setCaptchaValue(null);
      setCurrentStep(1);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (err) {
      console.error("Error sending enterprise quote request:", err);
      toast({
        title: "Error",
        description: "Failed to send quote request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const steps = [
    { number: 1, label: "Basic Info" },
    { number: 2, label: "Company Details" },
    { number: 3, label: "Your Requirements" }
  ];

  return (
    <Card className="p-8 bg-card border-0 shadow-lg max-w-3xl mx-auto">
      {/* Step Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-colors ${
                    currentStep === step.number
                      ? "border-primary bg-primary text-primary-foreground"
                      : currentStep > step.number
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-muted-foreground/30 bg-background text-muted-foreground"
                  }`}
                >
                  {step.number}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:block ${
                    currentStep === step.number
                      ? "text-primary"
                      : currentStep > step.number
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className="flex-1 h-[2px] mx-4 bg-border">
                  <div
                    className={`h-full transition-all duration-300 ${
                      currentStep > step.number ? "bg-primary" : "bg-transparent"
                    }`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <form id="enterprise-quote-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Basic Info */}
        {currentStep === 1 && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <h2 className="text-2xl font-bold text-center mb-6">Tell us about yourself</h2>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-foreground flex items-center gap-2">
                <User className="h-4 w-4" />
                Name *
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Your full name"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="work-email" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Work Email *
              </Label>
              <Input
                id="work-email"
                type="email"
                value={formData.workEmail}
                onChange={(e) => handleInputChange("workEmail", e.target.value)}
                placeholder="Your email address"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone number *
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                placeholder="Your phone number"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="job-title" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Job title *
              </Label>
              <Input
                id="job-title"
                type="text"
                value={formData.jobTitle}
                onChange={(e) => handleInputChange("jobTitle", e.target.value)}
                placeholder="Your role/position"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="linkedin-profile" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Linkedin className="h-4 w-4" />
                LinkedIn profile *
              </Label>
              <Input
                id="linkedin-profile"
                type="url"
                value={formData.linkedinProfile}
                onChange={(e) => handleInputChange("linkedinProfile", e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
                className="w-full"
                required
              />
            </div>
          </div>
        )}

        {/* Step 2: Company Details */}
        {currentStep === 2 && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <h2 className="text-2xl font-bold text-center mb-6">Company Details</h2>

            <div className="space-y-2">
              <Label htmlFor="company-name" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Building className="h-4 w-4" />
                Company name *
              </Label>
              <Input
                id="company-name"
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange("companyName", e.target.value)}
                placeholder="Your company name"
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-url" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Company URL
              </Label>
              <Input
                id="company-url"
                type="url"
                value={formData.companyUrl}
                onChange={(e) => handleInputChange("companyUrl", e.target.value)}
                placeholder="https://yourcompany.com"
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location" className="text-sm font-medium text-foreground flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Location (Country)
              </Label>
              <Input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                placeholder="Your country"
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* Step 3: Your Requirements */}
        {currentStep === 3 && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <h2 className="text-2xl font-bold text-center mb-6">Your Requirements</h2>

            <div className="space-y-2">
              <Label htmlFor="number-of-seats" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                How many user seats would you require? *
              </Label>
              <Textarea
                id="number-of-seats"
                value={formData.numberOfSeats}
                onChange={(e) => handleInputChange("numberOfSeats", e.target.value)}
                placeholder="e.g., 50 seats, 100+ users, or describe your team size..."
                className="w-full min-h-[40px] resize-none"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="primary-use-case" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Target className="h-4 w-4" />
                What are you planning to use gimmefy for? *
              </Label>
              <Textarea
                id="primary-use-case"
                value={formData.primaryUseCase}
                onChange={(e) => handleInputChange("primaryUseCase", e.target.value)}
                placeholder="Tell us about your main marketing challenges and goals..."
                className="w-full min-h-[100px] resize-none"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="using-ai-tools" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                Are you currently using any AI tools?
              </Label>
              <select
                id="using-ai-tools"
                value={formData.usingAiTools}
                onChange={(e) => handleInputChange("usingAiTools", e.target.value)}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional-requirements" className="text-sm font-medium text-foreground">
                Are there any additional requirements that we should know of?
              </Label>
              <Textarea
                id="additional-requirements"
                value={formData.additionalRequirements}
                onChange={(e) => handleInputChange("additionalRequirements", e.target.value)}
                placeholder="Any other information you'd like to share..."
                className="w-full min-h-[100px] resize-none"
              />
            </div>

            {/* reCAPTCHA */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Security Verification</span>
              </div>
              <ReCAPTCHA
                id="enterprise-recaptcha"
                ref={recaptchaRef}
                sitekey={recaptchaSiteKey}
                onChange={handleCaptchaChange}
                theme="light"
              />
              {!captchaValue && (
                <p className="text-sm text-muted-foreground mt-2">
                  Please complete the reCAPTCHA verification above
                </p>
              )}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between pt-6">
          {currentStep > 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              className="px-6"
            >
              Back
            </Button>
          ) : (
            <div />
          )}

          {currentStep < 3 ? (
            <Button
              type="button"
              onClick={handleNext}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 ml-auto group"
            >
              Continue
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          ) : (
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 ml-auto group"
              disabled={isSubmitting || !captchaValue}
            >
              {isSubmitting ? "Sending..." : "Submit"}
              {!isSubmitting && <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
};

export default EnterpriseQuoteForm;